import React, { Dispatch, SetStateAction } from 'react';
import { search } from 'sr-types';
import { StepperProvider } from '../components/StepperContext';
import { Filter } from '../types/savedFilters';
import type { EntityReportAdapter, ReportTemplateDataValue } from './adapters/EntityReportAdapter';
import GenerateReport from './GenerateReport';

type ReportRuntimeDataProps<TRawRow extends Record<string, unknown> = Record<string, unknown>> = {
    latestSearchResults?: TRawRow[];
    previewData?: ReportTemplateDataValue;
    sortOrders?: (string | number)[][];
    reportLoading?: boolean;
    onFetchReportData?: (overrides?: { sortBy?: string }) => void | Promise<void>;
};

export type ReportDataProvider<TRawRow extends Record<string, unknown> = Record<string, unknown>> =
    ReportRuntimeDataProps<TRawRow>;

interface ReportStepperProps<
    TRawRow extends Record<string, unknown> = Record<string, unknown>
> extends ReportRuntimeDataProps<TRawRow> {
    setReportVisible: Dispatch<SetStateAction<boolean>>;
    reportVisible: boolean;
    facets?: search.SkFacetSet[];
    appliedFilters?: Filter[];
    entityReportAdapter: EntityReportAdapter<TRawRow>;
    preferenceEntityKey: string;
    reportContextTerm?: string;
    reportDataProvider?: ReportDataProvider<TRawRow>;
}

export default <TRawRow extends Record<string, unknown> = Record<string, unknown>>({
    setReportVisible,
    reportVisible,
    latestSearchResults,
    previewData,
    sortOrders,
    onFetchReportData,
    reportLoading,
    facets,
    appliedFilters,
    entityReportAdapter,
    preferenceEntityKey,
    reportContextTerm,
    reportDataProvider
}: ReportStepperProps<TRawRow>) => {
    const resolvedLatestSearchResults = reportDataProvider?.latestSearchResults ?? latestSearchResults;
    const resolvedPreviewData = reportDataProvider?.previewData ?? previewData;
    const resolvedSortOrders = reportDataProvider?.sortOrders ?? sortOrders;
    const resolvedOnFetchReportData = reportDataProvider?.onFetchReportData ?? onFetchReportData;
    const resolvedReportLoading = reportDataProvider?.reportLoading ?? reportLoading;

    return (
        <StepperProvider>
            <GenerateReport
                setReportVisible={setReportVisible}
                reportVisible={reportVisible}
                latestSearchResults={resolvedLatestSearchResults}
                previewData={resolvedPreviewData}
                sortOrders={resolvedSortOrders}
                onFetchReportData={resolvedOnFetchReportData}
                reportLoading={resolvedReportLoading}
                facets={facets}
                appliedFilters={appliedFilters}
                entityReportAdapter={entityReportAdapter}
                preferenceEntityKey={preferenceEntityKey}
                reportContextTerm={reportContextTerm}
            />
        </StepperProvider>
    );
};
